/**
 * Utility functions for dropdown components
 */

import { useEffect, useMemo, useState } from "react";

/**
 * Custom hook to extract items from query data
 * @param {Object} queryData - Data returned from GraphQL query
 * @param {string} dataKey - Key to access items in the query data (default: "items")
 * @returns {Array} - Extracted items array
 */
export const useQueryItems = (queryData, dataKey = "items") => {
  return useMemo(() => {
    const keys = Object.keys(queryData || {});
    if (!keys.length) return [];
    const rootKey = keys[0];
    return queryData?.[rootKey]?.[dataKey] || [];
  }, [queryData, dataKey]);
};

/**
 * Custom hook to convert items to dropdown options
 * @param {Array} items - Array of items
 * @param {string} valueKey - Key to use for option id (default: "id")
 * @param {string} labelKey - Key to use for option display text (default: "name")
 * @returns {Array} - Formatted options for dropdown
 */
export const useDropdownOptions = (items, valueKey = "id", labelKey = "name") => {
  return useMemo(
    () =>
      items.map((item) => ({
        id: item[valueKey],
        displayText: item[labelKey],
      })),
    [items, valueKey, labelKey]
  );
};

/**
 * Custom hook to handle dropdown selection state
 * @param {any} data - Current form data value
 * @param {Array} options - Available dropdown options
 * @param {string} selection - Selection mode ("single" or "multiple")
 * @returns {Array} - [selected, setSelected, handleSelect]
 */
export const useDropdownSelection = (
  data,
  options,
  selection,
  handleChange,
  path
) => {
  const [selected, setSelected] = useState(undefined);

  // Update selected state when data or options change
  useEffect(() => {
    if (!data || options.length === 0) {
      setSelected(undefined);
      return;
    }

    if (selection === "multiple" && Array.isArray(data)) {
      const selectedOptions = options.filter((opt) => data.includes(opt.id));
      setSelected(selectedOptions);
    } else if (selection === "single") {
      const selectedOption = options.find((opt) => opt.id === data);
      setSelected(selectedOption ? [selectedOption] : null);
    }
  }, [data, options, selection]);

  // Handle selection changes
  const handleSelect = (val) => {
    setSelected(val);

    if (selection === "multiple") {
      const selectedIds = val?.map((v) => v.id) || [];
      handleChange(path, selectedIds);
    } else {
      const selectedId = val?.[0]?.id || null;
      handleChange(path, selectedId);
    }
  };

  return [selected, handleSelect];
};

/**
 * Creates a base dropdown component with common functionality
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Dropdown component
 */
export const createBaseDropdown = (Component) => {
  return (props) => {
    const { visible, uischema } = props;

    if (!visible) return null;

    const {
      readonly,
      disabled,
      placeholder,
      selection = "single",
    } = uischema.options || {};

    return Component({
      ...props,
      selection,
      readonly,
      disabled,
      placeholder,
      isDisabled:
        readonly ||
        disabled ||
        uischema?.options?.readonly ||
        uischema?.options?.disabled,
    });
  };
};
